import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Card,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { MailIcon, PhoneIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { formatPhoneNumber } from '@modules/_shared/components/PhoneInput';
import { TransactionCard } from '@modules/_shared/components/TransactionCard';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import {
  type ContactRequest,
  mapEnquiryToContactRequest,
} from '@modules/_shared/utils/contactRequest';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { translateAppraisalReason } from '@modules/_shared/utils/lead-labels';
import { LeadDetailsDrawer } from '@modules/lead-details/LeadDetailsDrawer';
import { GET_INBOX_MESSAGE } from '@modules/visibility-inbox/visibilityInboxQueries';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export const VisibilityInboxDrawerContent: React.FC<{
  contactRequest: ContactRequest | null;
}> = ({ contactRequest }) => {
  const { t, locale } = useLocale();

  if (!contactRequest) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('No contact request found')}
      </Alert>
    );
  }

  const currency = getCurrencyByCountryCode(
    contactRequest.property?.country_code ?? 'CH',
  );

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="h6">
        {t('This person has requested to contact you')}
      </Typography>
      <div>
        <Typography variant="subtitle2" gutterBottom>
          {t('Contact details')}
        </Typography>
        <Stack
          component={Card}
          variant="outlined"
          spacing={1}
          sx={{
            p: 2,
          }}
        >
          {contactRequest.contact?.full_name && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <UserIcon size={18} />
              <Typography variant="body1">
                {contactRequest.contact.full_name}
              </Typography>
            </Stack>
          )}
          {(contactRequest.contact?.phone_numbers?.length ?? 0) > 0 && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              {contactRequest.contact?.phone_numbers.map(phone => (
                <Stack
                  direction="row"
                  spacing={1}
                  key={phone.number}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <PhoneIcon size={18} />
                  <Link
                    key={phone.number}
                    variant="body1"
                    color="blue"
                    href={`tel:${phone.number}`}
                  >
                    {formatPhoneNumber(phone.number)}
                  </Link>
                </Stack>
              ))}
            </Stack>
          )}
          {(contactRequest.contact?.emails?.length ?? 0) > 0 && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              {contactRequest.contact?.emails.map(email => (
                <Stack
                  direction="row"
                  spacing={1}
                  key={email.email}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <MailIcon size={18} />

                  <Typography key={email.email} variant="body1">
                    {email.email}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </div>
      {contactRequest.property && (
        <div>
          <Typography variant="subtitle2" gutterBottom>
            {t('Property details')}
          </Typography>
          <Stack
            component={Card}
            variant="outlined"
            spacing={0}
            sx={{
              p: 2,
            }}
          >
            {contactRequest.property?.postcode != null && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
              >
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {t('Address')}:
                </Typography>
                {formatAddress(contactRequest.property, ', ')}
              </Typography>
            )}
            {contactRequest.property?.property_type?.label != null && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
              >
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {t('Property type')}:
                </Typography>
                {contactRequest.property.property_type.label}
              </Typography>
            )}

            {(contactRequest.property?.living_surface != null ||
              contactRequest.property?.built_surface != null) && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
              >
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {contactRequest.property?.country_code === 'ES'
                    ? t('Built surface')
                    : t('Living surface')}
                  :
                </Typography>
                {contactRequest.property.country_code === 'ES'
                  ? contactRequest.property.built_surface
                  : contactRequest.property.living_surface}
                {' m²'}
              </Typography>
            )}
            {contactRequest.property?.land_surface != null && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
              >
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {t('Land surface')}:
                </Typography>
                {contactRequest.property.land_surface} m²
              </Typography>
            )}
            {contactRequest.property?.number_of_rooms != null && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
              >
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {t('Number of rooms')}:
                </Typography>
                {contactRequest.property.number_of_rooms}
              </Typography>
            )}
            {contactRequest.property?.latest_appraisal?.min != null &&
              contactRequest.property?.latest_appraisal?.max != null && (
                <Typography
                  variant="body1"
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
                >
                  <Typography
                    component="span"
                    variant="subtitle1"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {t('Latest appraisal')}:
                  </Typography>
                  {`${formatPrice(
                    contactRequest.property.latest_appraisal.min,
                    locale,
                    currency,
                  )} - ${formatPrice(
                    contactRequest.property.latest_appraisal.max,
                    locale,
                    currency,
                  )}`}
                </Typography>
              )}
          </Stack>
        </div>
      )}
      <div>
        <Typography variant="subtitle2" gutterBottom>
          {t('Recipient')}
        </Typography>
        <Stack
          component={Card}
          variant="outlined"
          spacing={1}
          sx={{
            p: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <ShieldCheckIcon size={18} />
            <Typography variant="body1">
              {contactRequest.agent?.full_name ?? contactRequest.team?.name}
            </Typography>
          </Stack>
        </Stack>
      </div>
      <div>
        <Typography variant="subtitle2" gutterBottom>
          {t('Contact reason')}
        </Typography>
        <Stack
          component={Card}
          variant="outlined"
          spacing={1}
          sx={{
            p: 2,
          }}
        >
          <Typography variant="body1">
            {translateAppraisalReason(t, contactRequest.reason)}
          </Typography>
        </Stack>
      </div>
      <div>
        <Typography variant="subtitle2" gutterBottom>
          {t('Message')}
        </Typography>
        <Box
          component={Card}
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {contactRequest.message}
          </Typography>
        </Box>
      </div>
      {contactRequest.transaction != null && (
        <div>
          <Typography variant="subtitle2" gutterBottom>
            {t('Transactions')}
          </Typography>
          <RouterLink
            to={`/visibility/transactions/${contactRequest.transaction.id}`}
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <TransactionCard transaction={contactRequest.transaction} />
          </RouterLink>
        </div>
      )}
    </Stack>
  );
};

export const VisibilityInboxDrawer = () => {
  const { t } = useLocale();
  const navigate = useNavigateWithSearchParams();
  const { inboxId } = useParams();

  const { data, error, loading } = useQuery(GET_INBOX_MESSAGE, {
    variables: { id: inboxId ?? '' },
    skip: inboxId == null,
  });

  const leadId = data?.visibility_inbox_lead_agents[0]?.lead_id ?? null;

  const handleClose = () => {
    emitFeedRefresh();
    navigate('../');
  };

  let unifiedItem: ContactRequest | null = null;

  if (data?.enquiries_by_pk) {
    unifiedItem = mapEnquiryToContactRequest(data.enquiries_by_pk);
  }

  if (leadId != null) {
    return <LeadDetailsDrawer leadId={leadId} onClose={handleClose} />;
  }

  return (
    <Drawer
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
        },
      }}
      anchor="right"
      title={t('Contact request')}
      maxWidth={540}
    >
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}

      {loading ? (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      ) : (
        <VisibilityInboxDrawerContent contactRequest={unifiedItem} />
      )}
    </Drawer>
  );
};
